import { Card } from '@/components/atoms/Card';
import { Typography } from '@/components/atoms/Typography';
import { CircleCheckIcon } from '@/components/atoms/icons/CircleCheckIcon';
import { CircleIcon } from '@/components/atoms/icons/CircleIcon';
import { HeadsetIcon } from '@/components/atoms/icons/HeadsetIcon';
import { PhoneIcon } from '@/components/atoms/icons/PhoneIcon';
import { LogoHeader } from '@/components/molecules/header/LogoHeader';
import { useWebDocumentTitle } from '@/core/hooks/useWebDocumentTitle';
import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { useCatalogDynatraceView } from '@/core/hooks/useCatalogDynatraceView';
import { useIsAdhaJourney, useTranslation, useUIColors } from '@/core/hooks';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { openSupportPhoneDialer } from '@/core/utils/phoneUtils';
import React from 'react';
import { Platform, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export interface ServiceRequestDetailsProps {
    id: string;
    unit?: string;
    category: string;
    subCategory?: string;
    title: string;
    status: string;
    createdAt: string;
    description?: string;
    updates?: { title: string; timestamp: string; status?: 'default' | 'active' }[];
    caseNumber?: string;
}

const IS_WEB = Platform.OS === 'web';

function DetailField({
    label,
    value,
}: Readonly<{
    label: string;
    value: string;
}>) {
    return (
        <View style={[styles.fieldBlock, IS_WEB && styles.fieldBlockWeb]}>
            <Typography
                variant={IS_WEB ? 'caption' : 'subhead'}
                color="tertiary"
                style={!IS_WEB ? styles.detailsLabelMobile : undefined}
            >
                {label}
            </Typography>
            <Typography
                variant="body"
                color="primary"
                style={IS_WEB ? styles.fieldValueWeb : undefined}
            >
                {value}
            </Typography>
        </View>
    );
}

function StatusField({
    label,
    statusText,
    isResolved,
    isRTL,
    successColor,
    tertiaryColor,
}: Readonly<{
    label: string;
    statusText: string;
    isResolved: boolean;
    isRTL: boolean;
    successColor: string;
    tertiaryColor: string;
}>) {
    return (
        <View style={[styles.fieldBlock, IS_WEB && styles.fieldBlockWeb]}>
            <Typography
                variant={IS_WEB ? 'caption' : 'subhead'}
                color="tertiary"
                style={!IS_WEB ? styles.detailsLabelMobile : undefined}
            >
                {label}
            </Typography>
            <View style={[styles.statusRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                {isResolved ? (
                    <CircleCheckIcon size={12} color={successColor} />
                ) : (
                    <CircleIcon size={12} color={tertiaryColor} />
                )}
                <Typography
                    variant="footnote"
                    style={{
                        color: isResolved ? successColor : tertiaryColor,
                        marginLeft: isRTL ? 0 : 2,
                        marginRight: isRTL ? 2 : 0,
                    }}
                >
                    {statusText}
                </Typography>
            </View>
        </View>
    );
}

function ContactSupportBar({
    label,
    backgroundColor,
    bottomInset,
    primaryColor,
    buttonColor,
}: Readonly<{
    label: string;
    backgroundColor: string;
    bottomInset: number;
    primaryColor: string;
    buttonColor: string;
}>) {
    return (
        <TouchableOpacity
            style={[
                styles.contactRow,
                {
                    backgroundColor,
                    paddingBottom: 16 + bottomInset,
                },
            ]}
            activeOpacity={0.7}
            onPress={openSupportPhoneDialer}
        >
            {IS_WEB ? (
                <HeadsetIcon size={18} color={primaryColor} />
            ) : (
                <PhoneIcon size={15} color={buttonColor} />
            )}
            <Typography
                variant={IS_WEB ? 'body' : 'headlineLarge'}
                color="primary"
                style={[
                    styles.contactText,
                    !IS_WEB && {
                        fontFamily: 'Matter-SemiBold',
                        fontSize: 16,
                        lineHeight: 16,
                        color: buttonColor,
                    },
                ]}
            >
                {label}
            </Typography>
        </TouchableOpacity>
    );
}

export function ServiceRequestDetailsScreen(props: Readonly<ServiceRequestDetailsProps>) {
    useCatalogDynatraceView(DT_SCREEN.SERVICE_REQUEST_DETAIL, 'service_request_detail_viewed', true);
    const { t } = useTranslation();
    const colors = useUIColors();
    const isAdhaJourney = useIsAdhaJourney();
    const { isRTL } = useI18nContext();
    const insets = useSafeAreaInsets();
    const isResolved = (props.status || '').toLowerCase() === 'closed';
    const showUnitField = IS_WEB && !isAdhaJourney;

    const issueTitle = props.title.trim();
    const documentTitle = issueTitle.length > 0 ? issueTitle : t('support.detailsTitle');
    useWebDocumentTitle(IS_WEB ? documentTitle : undefined);

    return (
        <View style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]}>
            <LogoHeader showLanguageSwitch={false} hideLogo={true} />

            <ScrollView
                style={styles.scrollView}
                contentContainerStyle={[
                    styles.scrollContent,
                    {
                        paddingTop: IS_WEB ? 8 : 32,
                    },
                ]}
                showsVerticalScrollIndicator={false}
            >
                <View style={styles.headerSection}>
                    <Typography variant="largeTitle" color="primary" style={styles.title}>
                        {t('support.detailsTitle')}
                    </Typography>
                </View>

                <Card
                    padding={16}
                    style={{
                        backgroundColor: colors.backgroundLevel2,
                        borderRadius: 16,
                        shadowColor: 'transparent',
                        shadowOffset: { width: 0, height: 0 },
                        shadowOpacity: 0,
                        shadowRadius: 0,
                        elevation: 0,
                    }}
                >
                    {showUnitField ? (
                        <View style={[styles.fieldBlock, styles.fieldBlockWeb]}>
                            <Typography variant="caption" color="tertiary">
                                {t('support.detailsUnit')}
                            </Typography>
                            <Typography
                                variant="body"
                                color="primary"
                                style={styles.fieldValueWeb}
                            >
                                {props.unit || '-'}
                            </Typography>
                        </View>
                    ) : null}

                    <DetailField
                        label={t('support.detailsCategory')}
                        value={props.category}
                    />
                    <DetailField
                        label={t('support.detailsIssueDescription')}
                        value={
                            props.description ||
                            t('support.detailsNoDescription')
                        }
                    />
                    <DetailField
                        label={t('support.detailsCaseNumber')}
                        value={props.caseNumber || '-'}
                    />
                    <StatusField
                        label={t('support.detailsStatus')}
                        statusText={
                            props.status || t('support.inProgress')
                        }
                        isResolved={isResolved}
                        isRTL={isRTL}
                        successColor={colors.fontSuccessText}
                        tertiaryColor={colors.fontTertiary}
                    />
                </Card>

                <View style={styles.footerSpacer} />
            </ScrollView>

            <ContactSupportBar
                label={t('support.detailsContactSupport')}
                backgroundColor={colors.backgroundLevel1}
                bottomInset={insets?.bottom || 0}
                primaryColor={colors.fontPrimary}
                buttonColor={colors.buttonDefault}
            />
        </View>
    );
}

const styles = StyleSheet.create({
    container: { flex: 1 },
    scrollView: { flex: 1 },
    scrollContent: {
        paddingHorizontal: 24,
        paddingBottom: 32,
        gap: 16,
    },
    headerSection: { gap: 8 },
    title: {
        fontSize: IS_WEB ? 34 : 22,
        lineHeight: IS_WEB ? 40 : 22,
        fontWeight: '500',
    },
    fieldBlock: { marginBottom: 16, gap: 4 },
    fieldBlockWeb: { gap: 8 },
    fieldValueWeb: { lineHeight: 22 },
    statusRow: { alignItems: 'center', gap: 2 },
    contactRow: {
        paddingTop: 16,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        justifyContent: 'center',
    },
    contactText: { textDecorationLine: 'underline' },
    footerSpacer: { height: 24 },
    detailsLabelMobile: {
        fontFamily: 'Matter-Regular',
        fontSize: 14,
        lineHeight: 14,
    },
});

export default ServiceRequestDetailsScreen;
